/**
 * <ats-pdp-highlights> — the in-column PDP highlighted-reviews carousel.
 *
 * ⚠️ THIS IS NOT assets/ats-rail.js AND DELIBERATELY DOES NOT REUSE IT. That
 * element steps by exactly TWO cards whatever the viewport, which is right for
 * the product rails it was built for and wrong here: this carousel is one card
 * per view at every width, so a two-card step would skip a review on every
 * click. That reasoning was settled on the reverted full-width version
 * (8e454f5) and it still holds; do not "unify" the two.
 *
 * ⚠️ THE CAROUSEL WORKS WITHOUT THIS FILE. The track is a plain overflow-x
 * scroller with scroll-snap, reachable by touch, trackpad and keyboard. What
 * this adds is the arrows' behaviour and the progress line. The arrows ship
 * `disabled` and are enabled on measure, so a script that never runs leaves two
 * inert buttons rather than two that promise movement and do nothing.
 *
 * Contract with snippets/ats-pdp-highlights.liquid:
 *   [data-hl-track]  the scrolling flex row
 *   [data-hl-dir]    an arrow, value -1 or 1
 *   [data-hl-fill]   the progress line's moving span
 *
 * Classic deferred script, no module, matching every other script we own.
 */
(function () {
  'use strict';

  if (window.customElements === undefined) return;
  if (window.customElements.get('ats-pdp-highlights')) return;

  var EDGE = 2; // px of slack when deciding "is this scrolled to the end"
  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ATSPdpHighlights() {
    return Reflect.construct(HTMLElement, [], ATSPdpHighlights);
  }

  ATSPdpHighlights.prototype = Object.create(HTMLElement.prototype, {
    constructor: { value: ATSPdpHighlights, writable: true, configurable: true },
  });
  Object.setPrototypeOf(ATSPdpHighlights, HTMLElement);

  ATSPdpHighlights.prototype.connectedCallback = function () {
    var self = this;

    this.__onScroll = function () { self.schedule(); };
    this.__onResize = function () { self.schedule(); };
    this.__onClick = function (event) { self.onClick(event); };

    var track = this.track();
    if (track) track.addEventListener('scroll', this.__onScroll, { passive: true });
    window.addEventListener('resize', this.__onResize);
    this.addEventListener('click', this.__onClick);

    this.schedule();
  };

  ATSPdpHighlights.prototype.disconnectedCallback = function () {
    var track = this.track();
    if (track) track.removeEventListener('scroll', this.__onScroll);
    window.removeEventListener('resize', this.__onResize);
    this.removeEventListener('click', this.__onClick);
  };

  /* Looked up fresh each time rather than cached, so nothing breaks if the
     subtree under this host is ever replaced. */
  ATSPdpHighlights.prototype.track = function () {
    return this.querySelector('[data-hl-track]');
  };

  /** Coalesce scroll and resize bursts into one frame. */
  ATSPdpHighlights.prototype.schedule = function () {
    var self = this;
    if (this.__frame) return;
    this.__frame = window.requestAnimationFrame(function () {
      self.__frame = 0;
      self.refresh();
    });
  };

  /**
   * One page: one card plus the gap.
   *
   * ⚠️ MEASURED, NOT ASSUMED FROM clientWidth. The card is flex 0 0 100% of the
   * track, so a card and the track are the same width and the 10px gap sits
   * BETWEEN them. Stepping by clientWidth alone would therefore fall 10px short
   * of the next card's start on every click, and the shortfall compounds: by
   * card five it is 40px and scroll-snap is silently correcting a drift the
   * arrows created. Stepping by (card + gap) lands on the snap point exactly.
   */
  ATSPdpHighlights.prototype.metrics = function () {
    var track = this.track();
    if (!track) return null;

    var card = track.firstElementChild;
    if (!card) return null;

    var gap = parseFloat(window.getComputedStyle(track).columnGap);
    if (!isFinite(gap)) gap = 0;

    var cardWidth = card.getBoundingClientRect().width;
    if (cardWidth <= 0) return null;

    return {
      track: track,
      step: cardWidth + gap,
      count: track.children.length,
      max: track.scrollWidth - track.clientWidth,
    };
  };

  ATSPdpHighlights.prototype.refresh = function () {
    var m = this.metrics();
    if (!m) return;

    var index = m.max <= EDGE ? 0 : Math.round(m.track.scrollLeft / m.step);
    if (index > m.count - 1) index = m.count - 1;
    if (index < 0) index = 0;

    var arrows = this.querySelectorAll('[data-hl-dir]');
    for (var i = 0; i < arrows.length; i++) {
      var forward = Number(arrows[i].getAttribute('data-hl-dir')) > 0;
      arrows[i].disabled = forward
        ? m.track.scrollLeft >= m.max - EDGE
        : m.track.scrollLeft <= EDGE;
    }

    /* The line is one segment per card, positioned by index rather than by
       scrollLeft, so it agrees with the arrows by construction instead of
       drifting a fraction of a segment mid-scroll. */
    var fill = this.querySelector('[data-hl-fill]');
    if (fill) {
      var segment = 100 / m.count;
      fill.style.width = segment + '%';
      fill.style.left = index * segment + '%';
    }
  };

  ATSPdpHighlights.prototype.onClick = function (event) {
    var arrow = event.target.closest('[data-hl-dir]');
    if (!arrow || !this.contains(arrow)) return;

    var m = this.metrics();
    if (!m) return;

    m.track.scrollBy({
      left: m.step * Number(arrow.getAttribute('data-hl-dir')),
      behavior: motionQuery.matches ? 'auto' : 'smooth',
    });
  };

  window.customElements.define('ats-pdp-highlights', ATSPdpHighlights);
})();
